import type { CardSlotUiView } from '@kenocyber/game-domain';
import type { CardSlot } from '@kenocyber/shared';
import { BleedButtonLabel } from '../game/bleed-button-label';
import { CardHudBar } from '../ui/CardHudBar';
import type { PvpTeamFighterView, PvpTeamHudView } from './team-hud';

/**
 * The team match's combat interface.
 *
 * Presentation only: it is handed a view model and renders it. There is no
 * frame, no team arithmetic and no result logic here - all of that is
 * `pvpTeamHudView`, which is proven without a renderer.
 *
 * ## It sits AROUND the arena, never over it
 *
 * Three bars to the left, three to the right, the clock between them, along the
 * very top of the play area; the help line along the bottom. Nothing is placed
 * in the middle, because the middle is where six ships are shooting at each
 * other.
 *
 * ## The sides are the SERVER's, and so is their order
 *
 * "Sua equipe" is whichever chairs share this client's own team letter, and it
 * is computed once in the view model. Six browsers render six different screens
 * from one frame, and each of them is right.
 */

export interface PvpTeamHudProps {
  readonly view: PvpTeamHudView;
  readonly onArmTeleport?: () => void;
  /** Manda rebentar as marcas deste jogador (gate V28G.N). Ver o duelo. */
  readonly onDetonateBleed?: () => void;
  /** Os três espaços desta cadeira (gate V28H.J). Ver o duelo. */
  readonly cardSlots?: readonly CardSlotUiView[];
  /** A ORDEM de ligar uma carta. Ausente sem arena a quem a dar. */
  readonly onActivateCard?: (slot: CardSlot) => void;
  /** E a soltura da carga, quando o dedo sai (gate V28H.N). */
  readonly onReleaseCard?: ((slot: CardSlot) => void) | undefined;
}

export function PvpTeamHud({
  view,
  onArmTeleport,
  onDetonateBleed,
  cardSlots,
  onActivateCard,
  onReleaseCard,
}: PvpTeamHudProps) {
  return (
    <div className="pvp-hud pvp-team-hud" data-testid="pvp-team-hud">
      {/* A MESMA barra da caça e do duelo, no mesmo canto (gate V28H.J). */}
      <CardHudBar
        slots={cardSlots ?? []}
        enabled={onActivateCard !== undefined}
        onActivate={(slot) => onActivateCard?.(slot)}
        onRelease={(slot) => onReleaseCard?.(slot)}
      />
      <div className="pvp-hud-top">
        <PvpTeamColumn label="SUA EQUIPE" side="you" fighters={view.allies} />

        <div className="pvp-hud-centre">
          {view.timeLabel ? (
            <span className="pvp-hud-clock" data-testid="pvp-team-hud-time">
              {view.timeLabel}
            </span>
          ) : null}
          <span className="pvp-hud-phase" data-testid="pvp-team-hud-phase">
            {view.phaseLabel}
          </span>
          {/* Said where the fight is, not only in the lobby. */}
          <span className="pvp-hud-no-reward" data-testid="pvp-team-hud-no-reward">
            {view.noRewardNotice}
          </span>
          {view.reconnecting ? null : (
            <span
              className={`pvp-hud-connection tone-${view.connection}`}
              data-testid="pvp-team-hud-connection"
              role="status"
              aria-live="polite"
            >
              {view.connectionLabel}
            </span>
          )}
        </div>

        <PvpTeamColumn label="ADVERSÁRIOS" side="them" fighters={view.enemies} />
      </div>

      {view.prepareLabel ? (
        <p className="pvp-hud-prepare" data-testid="pvp-team-hud-prepare" role="status">
          {view.prepareLabel}
        </p>
      ) : null}

      {view.reconnecting ? (
        /*
         * Over the arena, which stays exactly as the last authoritative frame
         * left it. Nothing moves underneath: no invented movement, no invented
         * shot, and above all no invented defeat.
         */
        <div className="pvp-hud-reconnecting" data-testid="pvp-team-hud-reconnecting" role="alert">
          RECONECTANDO…
        </div>
      ) : null}

      {view.effects.length > 0 ? (
        <ul className="pvp-hud-effects" data-testid="pvp-team-hud-effects">
          {view.effects.map((effect) => (
            <li
              key={effect.kind}
              className={`pvp-hud-effect pvp-hud-effect-${effect.kind}`}
              data-testid={`pvp-team-hud-effect-${effect.kind}`}
            >
              {effect.kind === 'teleport' && onArmTeleport ? (
                <button
                  type="button"
                  className="pvp-hud-effect-arm"
                  data-testid="pvp-team-teleport-arm"
                  onClick={onArmTeleport}
                >
                  {effect.label}
                </button>
              ) : (
                <span className="pvp-hud-effect-label">{effect.label}</span>
              )}
              <span className="pvp-hud-effect-value">
                {effect.secondsLeft === null ? `x${effect.count}` : `${effect.secondsLeft}s`}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {/* **DETONAR** (gate V28G.N). O gémeo exato do duelo, e pelas mesmas razões. */}
      {onDetonateBleed ? (
        <button
          type="button"
          className="pvp-hud-detonate"
          data-testid="pvp-team-detonate"
          disabled={!view.canDetonate}
          onClick={onDetonateBleed}
        >
          <BleedButtonLabel marks={view.bleedMarks} />
        </button>
      ) : null}

      {view.controlsEnabled ? (
        <p className="pvp-hud-help" data-testid="pvp-team-hud-help">
          <span>WASD — mover</span>
          <span>Espaço — teleporte</span>
          <span>Mouse — mirar</span>
          <span>Clique — atirar</span>
          <span>Scroll — zoom</span>
        </p>
      ) : null}
    </div>
  );
}

function PvpTeamColumn({
  label,
  side,
  fighters,
}: {
  readonly label: string;
  readonly side: 'you' | 'them';
  readonly fighters: readonly PvpTeamFighterView[];
}) {
  if (fighters.length === 0) {
    return <div className={`pvp-team-column pvp-team-column-${side}`} />;
  }
  return (
    <div
      className={`pvp-team-column pvp-team-column-${side}`}
      data-testid={`pvp-team-column-${side}`}
    >
      <span className="pvp-team-column-label">{label}</span>
      {fighters.map((fighter) => (
        /*
         * Keyed by the participant id the ROOM named, never by the position in
         * this list. A roster that arrives in another order must produce the
         * same six bars rather than six remounted ones wearing each other's
         * numbers.
         */
        <div
          key={fighter.participantId}
          className={`pvp-fighter pvp-fighter-${side}${fighter.local ? ' pvp-fighter-local' : ''}`}
          data-testid={`pvp-team-fighter-${fighter.participantId}`}
          data-local={fighter.local ? 'true' : 'false'}
        >
          <span className="pvp-fighter-label">
            {fighter.local ? 'VOCÊ' : `POSIÇÃO ${fighter.slot}`}
            {fighter.connected ? null : <em className="pvp-fighter-offline"> — desconectado</em>}
          </span>
          <div
            className="pvp-fighter-track"
            role="progressbar"
            aria-label={`Vida — ${fighter.local ? 'você' : `posição ${fighter.slot}`}`}
            /* A razão exata em milli; o texto ao lado usa o formatador canónico. */
            aria-valuenow={Number(fighter.health.currentMilli)}
            aria-valuemin={0}
            aria-valuemax={Number(fighter.health.maxMilli)}
            aria-valuetext={`${fighter.health.currentText} de ${fighter.health.maxText}`}
            data-testid={`pvp-team-health-${fighter.participantId}`}
          >
            {/*
             * Width set inline because it is DATA, not style: it is the server's
             * number, and a stylesheet cannot hold a value that changes every
             * tick. No transition - damage is discrete.
             */}
            <div className="pvp-fighter-fill" style={{ width: `${fighter.health.percent}%` }} />
          </div>
          <span className="pvp-fighter-value">
            {fighter.health.currentText} / {fighter.health.maxText}
          </span>
        </div>
      ))}
    </div>
  );
}
